import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core'
import { ClaimInformation } from 'app/interfaces/claim-information';
import { CreateClaimTabInfo } from 'app/interfaces/create-claim-tab-info';
import { ApiService, UserInfo } from '../../../services/api.service'
import {
    ClaimRequestChecks,
    ClaimRequestDocument,
    ClaimRequestRequiredDocuments
} from '../../../interfaces/claim-request-required-documents'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { RadioComponent } from '@components/radio/radio.component'
import { ButtonComponent } from '@components/button/button.component'
import { FormsModule } from '@angular/forms'
import { ClaimDocumentationService } from '../../../services/claim-documentation.service'
import { formatBytes } from '../../../services/utils'
import { ConfirmModalComponent } from '@components/confirm-modal/confirm-modal.component'

@Component({
  selector: 'app-claim-documentation',
  standalone: true,
    imports: [
        TranslateModule,
        NgForOf,
        RadioComponent,
        NgIf,
        NgClass,
        ButtonComponent,
        FormsModule,
        ConfirmModalComponent
    ],
  templateUrl: './claim-documentation.component.html',
  styleUrl: './claim-documentation.component.scss'
})
export class ClaimDocumentationComponent implements OnInit {
    @Input() userInfo!: UserInfo
    @Input() claimInformation!: ClaimInformation
    @Input() previousStepWarranty: boolean
    @Output() tabInit = new EventEmitter<CreateClaimTabInfo>()
    @Output() updateClaim = new EventEmitter<ClaimInformation>()
    @Output() onContinueState = new EventEmitter<boolean>()

    @ViewChildren('claimDocumentInput') claimDocumentInputs: QueryList<ElementRef>

    acceptedDocuments = ['image/png', 'image/jpeg', 'application/pdf']
    acceptedDocumentsString = this.acceptedDocuments.join(',')
    maxDocumentSize = 12 * 1024 * 1024

    requiredDocuments: ClaimRequestRequiredDocuments = {
        checks: [],
        documents: [],
        additionalInfo: []
    };
    isSilestone: boolean;
    isSilestoneCheckNeeded: boolean;
    isMeshed: boolean;
    isMeshedCheckNeeded: boolean;

    isErrorModalVisible: boolean
    errorMessage: string;

    deletingDocuments: any[] = []

    constructor(
        private translate: TranslateService,
        private claimDocumentationService: ClaimDocumentationService,
        private api: ApiService
    ) {}

    ngOnInit() {
        this.tabInit.emit({
            currentTab: 'documentation',
            previousTab: this.previousStepWarranty ? 'warranty' : 'classification', // Warranty or classification
            nextTab: 'summary'
        })

        this.claimInformation.lastModifiedTab = 'documentation'

        this.initializeRequiredDocumentation()
    }

    initializeRequiredDocumentation(){
        const totalAmount = this.claimInformation.eurAmount
        const { claimClassification } = this.claimInformation
        const { selectedMaterials } = this.claimInformation
        this.isSilestone = false
        this.isMeshed = false
        selectedMaterials.forEach(mat => {
            if(mat.claimItem && mat.claimItem.productBrand && mat.claimItem.productBrand.toLowerCase().includes('silestone')) {
                this.isSilestone = true;
            }
            if(mat.invoiceData && mat.invoiceData.isMeshed){
                this.isMeshed = true;
            }
        })
        const country = this.userInfo.country

        let language = this.userInfo.language
        if(language){
            language = language.toLowerCase().slice(0, 2)
        } else {
            language = 'en'
        }

        const { productType, claimPlace, claimType } = claimClassification

        const hasWarranty = !!this.claimInformation.warrantyData.id

        this.requiredDocuments = this.claimDocumentationService.getRequiredDocumentation(
            this.claimInformation.documentation,
            productType,
            claimType,
            claimPlace,
            hasWarranty,
            this.isSilestone,
            totalAmount,
            this.isMeshed,
            language
        )

        this.isSilestoneCheckNeeded = this.requiredDocuments.documents.findIndex((search) => {
            return search.id === 'thermal_shock_picture'
        }) !== -1

        this.isMeshedCheckNeeded = this.requiredDocuments.documents.findIndex((search) => {
            return search.id === 'mesh_status_picture';
        }) !== -1

        this.onClaimDocumentationChange()
    }

    checkQuestion(i: number) {
        this.requiredDocuments.checks[i].isChecked = true
        this.onClaimDocumentationChange()
    }

    uncheckQuestion(i: number) {
        this.requiredDocuments.checks[i].isChecked = false
        this.onClaimDocumentationChange()
    }

    areQuestionsComplete() {
        for(let i = 0; i < this.requiredDocuments.checks.length; i++) {
            const question = this.requiredDocuments.checks[i]
            if(question.isChecked !== undefined) {
                continue;
            }
            // isChecked is undefined
            if(i === 0){
                return false;
            }

            // Check if previous requirements are met
            if(
                (!question.requiresPrevious && !question.requiresSpecificQuestion) ||
                (question.requiresPrevious && this.requiredDocuments.checks[i - 1].isChecked)
            ) {
                return false;
            }

            // Check if specific question requirements are met
            if(question.requiresSpecificQuestion){
                const requiredQuestion = this.requiredDocuments.checks.find((check) => check.id === question.requiresSpecificQuestion)
                if(requiredQuestion && (requiredQuestion.isChecked === undefined || requiredQuestion.isChecked)) {
                    return false;
                }
            }
        }

        return true
    }

    shouldShowQuestion(question: ClaimRequestChecks, questionIndex: number) {

        if(question.requiresSpecificQuestion) {
            const specificQuestionIndex = this.requiredDocuments.checks.findIndex(eachCheck => eachCheck.id === question.requiresSpecificQuestion)

            return this.requiredDocuments.checks[specificQuestionIndex].isChecked;
        }

        // If the question doesn't depend on previous questions
        if(!question.requiresPrevious){
            return true;
        }
        
        // If the question depends on the previous question being checked
        else if (question.requiresPrevious && this.requiredDocuments.checks[questionIndex - 1].isChecked) {
            return true;
        } 

        return false;
    }

    shouldShowDocument(document: ClaimRequestDocument) {
        if(document.shouldShow === false) {
            return false;
        }
        // If both requiresChecked and requiresUnchecked are empty return true
        if(document.requiresChecked.length === 0 && document.requiresUnchecked.length === 0) {
            return true;
        }

        let requiresChecked = true;

        document.requiresChecked.forEach(check => {
            const checkIndex = this.requiredDocuments.checks.findIndex(eachCheck => eachCheck.id === check)
            if(this.requiredDocuments.checks[checkIndex].isChecked === false) {
                requiresChecked = false;
            }
        })

        let requiresUnchecked = true;

        document.requiresUnchecked.forEach(check => {
            const checkIndex = this.requiredDocuments.checks.findIndex(eachCheck => eachCheck.id === check)
            if(this.requiredDocuments.checks[checkIndex].isChecked === true) {
                requiresUnchecked = false;
            }
        })

        return requiresChecked && requiresUnchecked;
    }

    onFileSelected($event: any, docIndex: number) {
        const files = $event.target.files
        const document = this.requiredDocuments.documents[docIndex]

        Array.from(files).forEach((file: any, index: number) => {
            // console.log(files,'files array from')
            // console.log(file, 'file ahora')
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                if (reader.result) {

                    // Check file type
                    let isDocumentTypeAccepted = false
                    for(let acceptedDocument of this.acceptedDocuments) {
                        if(file.type.match(acceptedDocument) || file.type === acceptedDocument) {
                            isDocumentTypeAccepted = true
                            break;
                        }
                    }
                    if(!isDocumentTypeAccepted) {
                        this.openErrorMessageModal(this.translate.instant('create-claim.documentation.invalid_file_type'))
                        this.clearHTMLInput(docIndex)
                        return
                    }

                    // Check file size
                    if(file.size > this.maxDocumentSize) {
                        this.openErrorMessageModal(this.translate.instant('create-claim.documentation.file_too_large'))
                        this.clearHTMLInput(docIndex)
                        return
                    }

                    this.api.uploadClaimDocument(this.claimInformation.claimInfo.id!, file, document.id, document.englishLabel).subscribe(res => {
                        document.document = res
                        this.claimInformation.documentation.documents.push(res)
                        this.claimInformation.updated = true
                        this.updateClaim.emit(this.claimInformation)
                        this.onContinueState.emit(this.canContinue())
                    })
                }
            }
        })
    }

    openErrorMessageModal(message: string) {
        this.isErrorModalVisible = true
        this.errorMessage = message
    }

    onDeleteFile(
        document: ClaimRequestDocument,
        documentIndex: number,
        silent = false
    ) {
        console.log('delete file')
        this.deletingDocuments.push(true);
        this.api.deleteClaimDocument(this.claimInformation.claimInfo.id!, document.document?.documentId!).subscribe({
            next: res => {
                if(res){
                    this.requiredDocuments.documents[documentIndex].document = undefined
                    for(let claimDocument of this.claimInformation.documentation.documents){
                        if(claimDocument.documentId === document.document?.documentId){
                            this.claimInformation.documentation.documents.splice(this.claimInformation.documentation.documents.indexOf(claimDocument), 1)
                            break;
                        }
                    }
                    this.clearHTMLInput(documentIndex)
                    this.claimInformation.updated = true
                    this.updateClaim.emit(this.claimInformation)
                    this.onContinueState.emit(this.canContinue())
                    this.deletingDocuments.pop()
                } else {
                    if(!silent){
                        alert('Error deleting file')
                    }
                    this.deletingDocuments.pop()
                }
            }, error: err => {
                if(!silent){
                    alert('Error deleting file')
                }
                this.deletingDocuments.pop()
            }
        })
    }

    clearHTMLInput(docIndex: number) {
        const fileInput = this.getFileInput(docIndex)
        if(fileInput){
            fileInput.nativeElement.value = ''
        }
    }

    onOpenFileSelect(index: number) {
        const fileInput = this.getFileInput(index)
        if(fileInput) {
            fileInput.nativeElement.click()
        } else {
            console.log('No file input found')
        }

        console.log('Clicked on file drop area')
    }

    getFileInput(index: number) {
        return this.claimDocumentInputs.find((input, i) => input.nativeElement.id === `claim_document__${index}`)
    }

    onClaimDocumentationChange() {
        for(let check of this.requiredDocuments.checks) {
            const checkInClaim =
                this.claimInformation.documentation.checks.find(
                    eachCheck => eachCheck.type === check.id
                );
            if(checkInClaim){
                checkInClaim.label = check.englishLabel
                checkInClaim.value = check.isChecked ? "true" : "false"
                if(check.additionalInfoId){
                    if(!checkInClaim.additionalInfo){
                        checkInClaim.additionalInfo = {}
                    }
                    checkInClaim.additionalInfo.type = check.additionalInfoId
                    checkInClaim.additionalInfo.value = check.isChecked ? check.additionalInfoValue : undefined
                    checkInClaim.additionalInfo.label = check.additionalInfoEnglishLabel
                }
            } else {
                const newCheck = {
                    type: check.id,
                    label: check.englishLabel,
                    value: check.isChecked ? "true" : "false",
                    additionalInfo: {}
                }
                if(check.additionalInfoId){
                    newCheck.additionalInfo = {
                        type: check.additionalInfoId,
                        value: check.isChecked ? check.additionalInfoValue : undefined,
                        label: check.additionalInfoEnglishLabel,
                    }
                }
                this.claimInformation.documentation.checks.push(newCheck);
            }
        }

        for(let additionalInfo of this.requiredDocuments.additionalInfo) {
            const additionalInfoInClaim =
                this.claimInformation.documentation.additionalInfo.find(
                    eachAdditionalInfo => eachAdditionalInfo.type === additionalInfo.id
                );
            if(additionalInfoInClaim){
                additionalInfoInClaim.label = additionalInfo.englishLabel
                additionalInfoInClaim.value = additionalInfo.value
            } else {
                this.claimInformation.documentation.additionalInfo.push({
                    type: additionalInfo.id,
                    label: additionalInfo.englishLabel,
                    value: additionalInfo.value
                })
            }
        }

        this.addAdhocChecks()
        this.updateClaim.emit(this.claimInformation)
        this.onContinueState.emit(this.canContinue())
    }

    addAdhocChecks(){
        if(this.isSilestoneCheckNeeded){
            const isItSilestoneCheck =
                this.claimInformation.documentation.checks.find(
                    eachCheck => eachCheck.type === 'is_it_silestone'
                );
            if(!isItSilestoneCheck){
                this.claimInformation.documentation.checks.push({
                    type: 'is_it_silestone',
                    label: 'Is it Silestone?',
                    value: this.isSilestone ? "true" : "false",
                })
            }
        }

        if(this.isMeshedCheckNeeded){
            const isItMeshedCheck =
                this.claimInformation.documentation.checks.find(
                    eachCheck => eachCheck.type === 'material_mesh_backed'
                );

            if(!isItMeshedCheck){
                this.claimInformation.documentation.checks.push({
                    type: 'material_mesh_backed',
                    label: 'Is the material mesh-backed?',
                    value: this.isMeshed ? "true" : "false",
                })
            }
        }
    }

    canContinue() {
        for(let i = 0; i < this.requiredDocuments.checks.length; i++) {
            const check = this.requiredDocuments.checks[i]
            if(!this.shouldShowQuestion(check, i)){
                check.isChecked = false;
            }
            if(check.additionalInfoIsRequired && check.isChecked && !check.additionalInfoValue){
                return false;
            }
        }
        for(let requiredDocument of this.requiredDocuments.documents) {
            if(this.shouldShowDocument(requiredDocument) && requiredDocument.isRequired && !requiredDocument.document) {
                return false;
            }
        }
        for(let additionalInfo of this.requiredDocuments.additionalInfo) {
            if(additionalInfo.shouldShow !== undefined && !additionalInfo.shouldShow){
                additionalInfo.value = ''
            }
            if(additionalInfo.isRequired && !additionalInfo.value) {
                return false;
            }
        }
        if(this.deletingDocuments.length === 0){
            this.deleteUnnecessaryDocuments()
        }

        return true;
    }

    private deleteUnnecessaryDocuments() {
        for(let i = 0; i < this.requiredDocuments.documents.length; i++){
            const document = this.requiredDocuments.documents[i];

            if(!this.shouldShowDocument(document) && document.document && document.document.versionId) {
                this.onDeleteFile(document, i, true)
            }
        }
    }

    protected readonly formatBytes = formatBytes

    downloadDocument(link: string | undefined) {
        if(!link) return

        const a = document.createElement('a')
        a.href = link
        a.target = '_blank'
        a.click()
    }
}
